async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body != null ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export interface ShareRow {
  id: string;
  buildId: string;
  sessionId: string | null;
  namespace: string;
  label: string | null;
  lockedEmail: string | null;
  status: string;
  expiresAt: string | null;
  createdBy: string;
  createdAt: string;
  buildDisplayName?: string | null;
  buildRegion?: string | null;
  dropCount?: number;
  token?: string;
  portalUrl?: string;
}

export interface ShareDrop {
  id: string;
  filename: string;
  sizeBytes: number;
  status: string;
  s3Key?: string;
  sha256?: string;
  errorMessage?: string | null;
  verifiedEmail?: string | null;
  createdAt: string;
}

export interface BuildRow {
  id: string;
  namespace: string;
  region: string;
  context: string | null;
  displayName: string;
  status: 'known' | 'unreachable' | 'unknown';
  config: Record<string, unknown> | null;
  lastSeenAt: string | null;
  probedAt: string | null;
}

export interface ContainerStatus {
  name: string;
  ready: boolean;
  restartCount: number;
  image: string;
  state: 'running' | 'waiting' | 'terminated' | 'unknown';
}

export interface K8sEvent {
  reason: string;
  message: string;
  timestamp: string;
  count: number;
  type: 'Normal' | 'Warning';
}

export interface PodStatusEvent {
  pod: { name: string; phase: string; startedAt: string | null } | null;
  containers: ContainerStatus[];
  events: K8sEvent[];
  error?: string;
}

export type MfaMethod = 'totp' | 'fastmail' | 'sms';

export interface MfaConfig {
  method: MfaMethod;
  totpSecret?: string;
  // Modern JMAP-style Fastmail (preferred)
  fastmail?: {
    token: string;
    fromAddress: string;
    subjectFilter: string;
    folder?: string;
  };
  // Legacy IMAP-style Fastmail (back-compat)
  fastmailUser?: string;
  fastmailAppPassword?: string;
  fastmailFolder?: string;
  fastmailCodePattern?: string;
  twilioAccountSid?: string;
  twilioAuthToken?: string;
  twilioFromNumber?: string;
  codeInputSelector?: string;
  codeSubmitSelector?: string;
}

export interface LoginConfig {
  loginPath: string;
  selectors: { username: string; password: string; submit: string; [extra: string]: string };
  iframeSelector?: string;
  postSubmitDismiss?: string;
  waitForUrl?: string;
  extractToken?:
    | { method: 'none' }
    | { method: 'network'; urlPattern: string; triggerUrl?: string }
    | { method: 'cookie'; cookieName: string }
    | { method: 'localStorage'; localStorageKey: string };
  otp?: { container: string; submitButton: string };
}

export interface EhrRow {
  id: string;
  slug: string;
  displayName: string;
  loginUrl: string | null;
  baseUrlTemplate: string;
  authStyle: string;
  loginSelectorsSource: string | null;
  loginConfig: LoginConfig | null;
  recipePath: string | null;
  mfaConfig: MfaConfig | null;
  stats: { sessions: number; lastRunAt: string | null; totalRecords: number; openConflicts: number };
  activeRecipeId: string | null;
  lastScopedAt: string | null;
  rescopeNeeded: boolean;
}

export interface ScopeRecipeRow {
  id: string;
  ehrId: string;
  yaml: string;
  status: 'pending_review' | 'accepted' | 'rejected';
  checksum: string;
  scopedAt: string | null;
  acceptedAt: string | null;
  acceptedBy: string | null;
}

export interface FlatFileRecipeRow {
  id: string;
  ehrId: string;
  sourceSystem: string;
  headerFingerprint: string;
  label: string | null;
  specs: Array<Record<string, unknown>>;
  docRules: Record<string, unknown> | null;
  version: number;
  status: 'pending_review' | 'accepted' | 'rejected' | 'superseded';
  health: Record<string, unknown> | null;
  checksum: string;
  authoredBy: string;
  rationale: string | null;
  supersededById: string | null;
  scopedAt: string | null;
  acceptedAt: string | null;
  acceptedBy: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Present on the cross-EHR list (joined).
  ehrSlug?: string;
  ehrDisplayName?: string;
}

export interface RecipeHealth {
  verdict: 'PASS' | 'FAIL';
  headerCoverage: number;
  passRate: number;
  refRate: number;
  sampleSize: number;
  missingColumns: string[];
  perSpec: Array<{ resourceType: string; ok: number; total: number; missingColumns: string[] }>;
  thresholds: { headerCoverage: number; passRate: number; refRate: number };
  checkedAt: string;
}

export interface DiscoveryStarted {
  discoveryRunId: string;
  ehrId: string;
  ehrSlug: string;
  topic: string;
}

export interface ScopeJobRow {
  id: string;
  ehrId: string;
  triggerUrl: string;
  status: string;
  tracePath: string | null;
  proposedRecipePath: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConflictRow {
  id: string;
  sessionId: string;
  sessionTitle: string | null;
  kind: string;
  severity: string;
  context: Record<string, unknown>;
  status: string;
  resolution: { action: string; note: string | null; resolvedBy: string; resolvedAt: string } | null;
  createdAt: string;
}

export interface SettingsData {
  jiraBaseUrl: string;
  jiraToken: string;
  jiraProject: string;
  jiraMaxResults: string;
  aiProvider: string;
  aiModel: string;
  ollamaUrl: string;
  ollamaModel: string;
  defaultUserRole: string;
  awsAccessKeyId: string;
  awsSecretAccessKey: string;
  awsSessionToken: string;
  awsRegion: string;
  infraSyncIntervalHours: string;
  githubToken: string;
  githubInfraOwner: string;
  githubInfraRepo: string;
  auto_scope_on_session_create: string;
  rescope_staleness_days: string;
}

export interface SessionRow {
  id: string; buildId: string | null; ehrId: string | null; title: string | null; description: string | null;
  status: string; startedAt: string | null; finishedAt: string | null; createdAt: string;
  sources: Array<{ kind: string; ehrSlug?: string; ehrId?: string; [k: string]: unknown }>;
  destinations: Array<{ kind: string; [k: string]: unknown }>;
  recipeId: string | null;
  scopingJobId: string | null;
  discoveryRunId: string | null;
  rescopeReason: string | null;
  warningCount?: number;
}

export interface ImportRow {
  id: string; sessionId: string; endpoint: string; status: string;
  totalExpected: number | null; totalFetched: number; createdAt: string;
}

export interface UserRow {
  id: string; email: string; displayName: string; role: string;
  isActive: boolean; lastLoginAt: string | null; createdAt: string;
}

export interface AuditLogRow {
  id: string; userId: string | null; userEmail: string | null; action: string;
  resource: string | null; resourceId: string | null; meta: Record<string, unknown> | null;
  ip: string | null; createdAt: string;
}

export interface KbArticle {
  id: string; ehrId: string | null; title: string; body?: string;
  authorName: string | null; lastEditedByName: string | null; lastEditedAt: string | null;
  createdAt: string; updatedAt: string;
}

export interface KbRevision {
  id: string;
  articleId: string;
  editorId: string | null;
  editorName: string | null;
  bodyBefore: string;
  bodyAfter: string;
  titleBefore: string | null;
  titleAfter: string | null;
  createdAt: string;
}

export interface KbComment {
  id: string; body: string; authorName: string | null; createdAt: string;
}

export interface JiraIssue {
  key: string; summary: string; status: string; statusCategory: string;
  statusOrder: number; priority: string | null; assignee: string | null;
  issueType: string; url: string;
  clientEmr: string | null; customerUrl: string | null;
  goLiveDate: string | null; sortDate: string | null;
}

export interface DashboardStats {
  recordsThisWeek: number;
  recordsPerDay: number[];
  conflictsByKindAndSeverity: Array<{
    kind: string;
    critical: number;
    high: number;
    medium: number;
    low: number;
  }>;
}

export interface SessionsOverTimePoint {
  date: string;
  completed: number;
  failed: number;
}

export interface QueueStats {
  queued: number;
  active: number;
  delayed: number;
  failed: number;
}

export interface ScopeReport {
  runId: string;
  ehrSlug: string;
  completedAt: string;
  loginSuccess: boolean;
  endpointsDiscovered: number;
  endpointsPassed: string[];
  endpointsFailed: Array<{ endpoint: string; kind: string; message: string }>;
  suspiciousEndpoints: string[];
  safetyBlocks: Array<{ method: string; url: string; reason: string; ts: string }>;
  autoCorrections: Array<{ endpoint: string; from: string; to: string; reason: string }>;
  preflightAttempts: number;
  coverageByResourceType: Record<string, { endpoint: string; status: 'passed' | 'failed'; reason?: string }>;
  screenshotSteps: string[];
  modelUsed: string;
}

export interface ScopeScreenshot {
  step: string;
  filename: string;
  url: string;
}

export interface StorageBucket {
  name: string;
  label: string;
  description: string;
  sensitive: boolean;
}
export interface StorageFolder {
  prefix: string;
  name: string;
}
export interface StorageObject {
  key: string;
  name: string;
  size: number;
  lastModified: string | null;
  storageClass: string | null;
}
export interface StorageListing {
  bucket: string;
  prefix: string;
  folders: StorageFolder[];
  objects: StorageObject[];
  nextToken: string | null;
  truncated: boolean;
}
export interface StorageObjectMeta {
  bucket: string;
  key: string;
  size: number;
  contentType: string;
  lastModified: string | null;
  etag: string | null;
  metadata: Record<string, string>;
  previewable: boolean;
  isImage: boolean;
  isPdf: boolean;
  bodyText: string | null;
  truncated: boolean;
}

export interface OpsService {
  id: string;
  name: string;
  description: string;
  schedule: string;
  configured: boolean;
  running: boolean;
  lastRunAt: string | null;
  lastDurationMs: number | null;
  lastSummary: string | null;
  lastError: string | null;
  lastTrigger: 'schedule' | 'manual' | null;
}
export interface OpsQueue {
  name: string;
  label: string;
  hasConsumer: boolean;
  counts: { waiting: number; active: number; completed: number; failed: number; delayed: number } | null;
  error?: string;
}
export interface OpsStatus {
  services: OpsService[];
  queues: OpsQueue[];
}

export const api = {
  getOps: () => request<OpsStatus>('/api/ops'),
  runOpsService: (id: string) => request<{ service: OpsService }>(`/api/ops/services/${encodeURIComponent(id)}/run`, { method: 'POST' }),
  listBuilds: () => request<{ builds: BuildRow[] }>('/api/builds'),
  getBuild: (id: string) => request<{ build: BuildRow; sessions: SessionRow[]; sessionStats: { total: number; completed: number; running: number; failed: number; draft: number } }>(`/api/builds/${id}`),
  listSessions: (buildId?: string) =>
    request<{ sessions: SessionRow[] }>(`/api/sessions${buildId ? `?buildId=${buildId}` : ''}`),

  // ── Share portal (client file-drop) ──────────────────────────────────────
  listShares: (params?: { buildId?: string; sessionId?: string; status?: string }) => {
    const qs = new URLSearchParams(
      Object.entries(params ?? {}).filter(([, v]) => v != null) as [string, string][],
    ).toString();
    return request<{ shares: ShareRow[] }>(`/api/shares${qs ? `?${qs}` : ''}`);
  },
  createShare: (body: { buildId: string; sessionId?: string; label?: string; lockedEmail?: string; expiresAt?: string | null }) =>
    request<{ share: ShareRow }>('/api/shares', { method: 'POST', body: JSON.stringify(body) }),
  getShare: (id: string) =>
    request<{ share: ShareRow; stats: { totalDrops: number; completeDrops: number; totalBytes: number } }>(`/api/shares/${id}`),
  updateShare: (id: string, body: { label?: string | null; lockedEmail?: string | null; expiresAt?: string | null; status?: 'active' | 'revoked' }) =>
    request<{ share: ShareRow }>(`/api/shares/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  revokeShare: (id: string) =>
    request<{ share: ShareRow }>(`/api/shares/${id}/revoke`, { method: 'POST', body: JSON.stringify({}) }),
  getShareContents: (id: string) =>
    request<{ drops: ShareDrop[] }>(`/api/shares/${id}/contents`),
  ingestShareDrops: (id: string, body: { dropIds: string[]; targetSessionId?: string }) =>
    request<{ ingested: Array<{ dropId: string; uploadId: string }>; failed: Array<{ dropId: string; error: string }>; sessionId: string }>(
      `/api/shares/${id}/ingest`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  listEhrs: () => request<{ ehrs: EhrRow[] }>('/api/ehrs'),
  getEhr: (id: string) =>
    request<{
      ehr: EhrRow;
      scopeJobs: ScopeJobRow[];
      sessions: SessionRow[];
      conflicts: ConflictRow[];
      sessionStats: { total: number; completed: number; running: number; failed: number; draft: number };
    }>(`/api/ehrs/${id}`),
  createEhr: (body: { slug: string; displayName: string; loginUrl: string; baseUrlTemplate?: string; authStyle?: string }) =>
    request<{ ehr: EhrRow }>('/api/ehrs', { method: 'POST', body: JSON.stringify(body) }),
  updateEhr: (ehrId: string, body: Partial<{ displayName: string; loginUrl: string | null; baseUrlTemplate: string; authStyle: string }>) =>
    request<{ ehr: EhrRow }>(`/api/ehrs/${ehrId}`, { method: 'PUT', body: JSON.stringify(body) }),
  saveMfaConfig: (ehrId: string, body: MfaConfig) =>
    request<{ ehr: EhrRow }>(`/api/ehrs/${ehrId}/mfa`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteMfaConfig: (ehrId: string) =>
    request<{ ehr: EhrRow }>(`/api/ehrs/${ehrId}/mfa`, { method: 'DELETE' }),
  saveLoginConfig: (ehrId: string, body: LoginConfig) =>
    request<{ ehr: EhrRow }>(`/api/ehrs/${ehrId}/login-config`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteLoginConfig: (ehrId: string) =>
    request<{ ehr: EhrRow }>(`/api/ehrs/${ehrId}/login-config`, { method: 'DELETE' }),
  startScopeJob: (ehrId: string, body: { username: string; password: string; loginUrl?: string; rememberCredentials?: boolean }) =>
    request<{ scopeJobId: string; ehrId: string; topic: string }>(`/api/ehrs/${ehrId}/scope`, { method: 'POST', body: JSON.stringify(body) }),
  getScopeJobRecipe: (scopeJobId: string) =>
    request<{ recipePath: string; recipeYaml: string; ehr: EhrRow }>(`/api/discovery/${scopeJobId}/recipe`),
  acceptRecipe: (scopeJobId: string, body?: { username?: string; password?: string; loginUrl?: string; canaryLimit?: number }) =>
    request<{ sessionId: string; seedEndpoints: string[]; topic: string }>(`/api/discovery/${scopeJobId}/accept`, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  rejectRecipe: (scopeJobId: string) =>
    request<{ ok: boolean }>(`/api/discovery/${scopeJobId}/reject`, { method: 'POST' }),
  listConflicts: (params?: { status?: string }) =>
    request<{ conflicts: ConflictRow[] }>(`/api/conflicts${params?.status ? `?status=${params.status}` : ''}`),
  getSettings: () => request<SettingsData>('/api/settings'),
  saveSettings: (body: SettingsData) => request<{ ok: true }>('/api/settings', { method: 'PUT', body: JSON.stringify(body) }),
  getMe: () => request<{ id: string; email: string; role: string; name: string; jiraPersonalToken: string | null; jiraEmail: string | null }>('/auth/me'),
  updateMe: (body: { jiraPersonalToken?: string | null; jiraEmail?: string | null }) => request<{ ok: true }>('/auth/me', { method: 'PUT', body: JSON.stringify(body) }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  health: () => request<{ status: string; ts: string }>('/api/health'),

  // sessions
  getSession: (id: string) => request<{ session: SessionRow }>(`/api/sessions/${id}`),
  createSession: (body: { buildId?: string; title?: string; description?: string; sources?: unknown[]; destinations?: unknown[] }) =>
    request<{ session: SessionRow }>('/api/sessions', { method: 'POST', body: JSON.stringify(body) }),
  getSessionImports: (id: string) => request<{ imports: ImportRow[] }>(`/api/sessions/${id}/imports`),

  // uploads
  uploadFileToSession: async (sessionId: string, file: File): Promise<{ upload: { id: string; filename: string; sizeBytes: number; headers: string[] | null; rowCountEstimate: number | null } }> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`/api/sessions/${sessionId}/uploads`, { method: 'POST', body: fd });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`${res.status} ${res.statusText}: ${text}`);
    }
    return res.json();
  },
  // Same endpoint as uploadFileToSession, but via XHR so the caller gets real
  // byte-level upload progress (fetch can't report request-body progress). Used
  // by the operator drop pad for per-file bars / throughput / ETA.
  uploadFileToSessionXHR: (
    sessionId: string,
    file: File,
    opts: { onProgress?: (loaded: number) => void; signal?: AbortSignal } = {},
  ): Promise<{ upload: { id: string; filename: string; sizeBytes: number; headers: string[] | null; rowCountEstimate: number | null } }> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/sessions/${sessionId}/uploads`);
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable && opts.onProgress) opts.onProgress(ev.loaded);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); } catch { reject(new Error('bad upload response')); }
        } else {
          reject(new Error(`${xhr.status} ${xhr.statusText}: ${(xhr.responseText || '').slice(0, 200)}`));
        }
      };
      xhr.onerror = () => reject(new Error('network error during upload'));
      xhr.onabort = () => reject(new Error('aborted'));
      if (opts.signal) opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
      const fd = new FormData();
      fd.append('file', file);
      xhr.send(fd);
    });
  },
  listUploads: (sessionId: string) =>
    request<{ uploads: Array<{ id: string; filename: string; sizeBytes: number; sha256: string; headers: string[] | null; rowCountEstimate: number | null; createdAt: string }> }>(`/api/sessions/${sessionId}/uploads`),
  getImportStats: (sessionId: string) =>
    request<{ stats: { rowsExtracted: number; rowsMapped: number; rowsValidated: number; rowsMatchedLink: number; rowsMatchedInsert: number; rowsMatchedReview: number; rowsLoaded: number; rowsQuarantined: number } | null }>(`/api/sessions/${sessionId}/stats`),
  listQuarantine: (sessionId: string) =>
    request<{ rows: Array<{ id: string; rowNumber: number; stage: string; reason: string; createdAt: string }> }>(`/api/sessions/${sessionId}/quarantine`),
  listIdMap: (sessionId: string, params?: { canonicalType?: string; search?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.canonicalType) q.set('canonicalType', params.canonicalType);
    if (params?.search) q.set('search', params.search);
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    const qs = q.toString();
    return request<{
      rows: Array<{ sourceSystem: string; sourceId: string; canonicalType: string; cerboId: number; matchTier: string; createdAt: string }>;
      summary: Array<{ canonicalType: string; matchTier: string; count: number }>;
      total: number;
      limit: number;
      offset: number;
    }>(`/api/sessions/${sessionId}/id-map${qs ? `?${qs}` : ''}`);
  },
  listReviewQueue: (sessionId: string) =>
    request<{ rows: Array<{ id: string; rowNumber: number; matchTier: string; reason: string; status: string; createdAt: string }> }>(`/api/sessions/${sessionId}/review`),
  decideReviewItem: (itemId: string, body: { decision: 'link_to_candidate' | 'insert_as_new' | 'reject' | 'file_to_patient'; patientId?: string }) =>
    fetch(`/api/review/${itemId}/decide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}: ${await r.text().catch(() => '')}`);
      return r.json() as Promise<{ jobId: string; itemId: string }>;
    }),
  bulkDecideReviewItems: (body: {
    itemIds: string[];
    decision: 'link_to_candidate' | 'insert_as_new' | 'reject';
  }) =>
    fetch(`/api/review/bulk-decide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(async (r) => {
      if (!r.ok) {
        const text = await r.text().catch(() => '');
        throw new Error(`${r.status} ${r.statusText}: ${text}`);
      }
      return r.json() as Promise<{ jobIds: string[]; count: number; sessionId: string }>;
    }),
  getReviewItem: (sessionId: string, itemId: string) =>
    request<{ item: {
      id: string;
      sessionId: string;
      rowNumber: number;
      canonicalRecord: Record<string, unknown>;
      candidateRecord: Record<string, unknown> | null;
      matchTier: string;
      reason: string;
      status: string;
      decisionKind: string | null;
      resolvedBy: string | null;
      resolvedAt: string | null;
      createdAt: string;
    } }>(`/api/sessions/${sessionId}/review/${itemId}`),

  // conflicts
  resolveConflict: (id: string, body: { action: 'overwrite' | 'skip' | 'flag'; note?: string }) =>
    request<{ conflict: unknown }>(`/api/conflicts/${id}/resolve`, { method: 'POST', body: JSON.stringify(body) }),

  // users
  listUsers: () => request<{ users: UserRow[] }>('/api/users'),
  getUser: (id: string) => request<{ user: UserRow }>(`/api/users/${id}`),
  updateUser: (id: string, body: { role?: string; isActive?: boolean }) =>
    request<{ user: UserRow }>(`/api/users/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  listAuditLogs: (params?: { userId?: string; limit?: number }) => {
    const p = new URLSearchParams();
    if (params?.userId) p.set('userId', params.userId);
    if (params?.limit) p.set('limit', String(params.limit));
    const qs = p.toString();
    return request<{ logs: AuditLogRow[] }>(`/api/audit-logs${qs ? `?${qs}` : ''}`);
  },
  getConflictCount: () => request<{ count: number }>('/api/conflicts/count'),

  // kb
  listKbArticles: (ehrId?: string) => request<{ articles: KbArticle[] }>(`/api/kb${ehrId ? `?ehrId=${ehrId}` : ''}`),
  getKbArticle: (id: string) => request<{ article: KbArticle; comments: KbComment[] }>(`/api/kb/${id}`),
  createKbArticle: (body: { ehrId?: string | null; title: string; body: string }) =>
    request<{ article: KbArticle }>('/api/kb', { method: 'POST', body: JSON.stringify(body) }),
  updateKbArticle: (id: string, body: { title?: string; body?: string }) =>
    request<{ article: KbArticle }>(`/api/kb/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  addKbComment: (articleId: string, body: { body: string }) =>
    request<{ comment: KbComment }>(`/api/kb/${articleId}/comments`, { method: 'POST', body: JSON.stringify(body) }),
  getKbRevisions: (id: string) => request<{ revisions: KbRevision[] }>(`/api/kb/${id}/revisions`),

  // dashboard
  getAiUsageStats: () => request<{
    totals: { calls24h: number; calls7d: number; tokensOut24h: number; tokensOut7d: number; avgDuration24h: number };
    byModel: Array<{ model: string; provider: string; calls: number; tokensOut: number; avgDuration: number }>;
    byContext: Array<{ context: string | null; calls: number; tokensOut: number }>;
    perDay: Array<{ date: string; calls: number; tokensOut: number }>;
  }>('/api/ai-usage/stats'),
  getDashboardStats: () => request<DashboardStats>('/api/stats'),
  getSessionsOverTime: (days = 30) =>
    request<{ data: SessionsOverTimePoint[] }>(`/api/stats/sessions-over-time?days=${days}`),
  getQueueStats: () => request<QueueStats>('/api/queue/stats'),
  getScopeReport: (runId: string) => request<ScopeReport>(`/api/discovery/${runId}/report`),
  getScopeScreenshots: (runId: string) => request<ScopeScreenshot[]>(`/api/discovery/${runId}/screenshots`),

  // jira
  listJiraIssues: (search?: string, ehrName?: string) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (ehrName) params.set('ehrName', ehrName);
    const qs = params.toString();
    return request<{ issues: JiraIssue[] }>(`/api/jira/issues${qs ? `?${qs}` : ''}`);
  },

  // session scoping
  scopeSession: (id: string) =>
    request<{ jobId: string }>(`/api/sessions/${id}/scope`, { method: 'POST' }),
  acceptScope: (id: string) =>
    request<{ ok: boolean }>(`/api/sessions/${id}/scope/accept`, { method: 'POST' }),
  rejectScope: (id: string) =>
    request<{ ok: boolean }>(`/api/sessions/${id}/scope/reject`, { method: 'POST' }),
  startSession: (id: string) =>
    request<{ sessionId: string; seedEndpoints: string[] }>(`/api/sessions/${id}/start`, { method: 'POST' }),
  pauseSession: (id: string) =>
    request<{ ok: boolean; status: string }>(`/api/sessions/${id}/pause`, { method: 'POST' }),
  resumeSession: (id: string) =>
    request<{ ok: boolean; status: string }>(`/api/sessions/${id}/resume`, { method: 'POST' }),
  abortSession: (id: string) =>
    request<{ ok: boolean; status: string; removedCache: number; removedCanonical: number }>(`/api/sessions/${id}/abort`, { method: 'POST' }),
  getScopeRecipe: (id: string) =>
    request<{
      recipe: {
        id: string;
        yaml: string;
        status: 'pending_review' | 'accepted' | 'rejected';
        scopedAt: string | null;
        acceptedAt: string | null;
        acceptedBy: string | null;
      };
      discoveryRun: { id: string; status: string | null } | null;
      scopeReport: unknown;
    }>(`/api/sessions/${id}/scope/recipe`),
  editScopeRecipe: (id: string, recipeYaml: string) =>
    request<{ recipeId: string; unchanged: boolean }>(`/api/sessions/${id}/scope/edit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ yaml: recipeYaml }),
    }),
  testScopeEndpoint: (id: string, endpointName: string, recipeYaml: string) =>
    request<{
      fetchStatus: number;
      records: unknown[];
      recordCount: number;
      pageHasMore: boolean;
      authFailed: boolean;
      fetchError?: string;
      rawSample: string;
      transform?: {
        ok: boolean;
        output?: unknown;
        error?: string;
        validationIssues?: Array<{ path: string; message: string }>;
        rawRecord?: unknown;
      };
    }>(`/api/sessions/${id}/scope/test-endpoint`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpointName, recipeYaml }),
    }),
  repairScopeEndpoint: (
    id: string,
    payload: {
      endpointName: string;
      recipeYaml: string;
      failure: {
        fetchStatus?: number;
        fetchError?: string;
        rawSample?: string;
        validationIssues?: Array<{ path: string; message: string }>;
      };
      exampleRecord?: unknown;
    },
  ) =>
    request<{
      endpointDef: Record<string, unknown>;
      transform: { resourceType: string; jsonata: string };
      rationale: string;
      patchedYaml: string;
      changed: Record<string, boolean>;
    }>(`/api/sessions/${id}/scope/repair-endpoint`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  // Cache browser
  getCacheSummary: (id: string) =>
    request<{
      session: { id: string; status: string; startedAt: string | null; finishedAt: string | null };
      imports: Array<{
        endpoint: string;
        parentId: string | null;
        status: string;
        totalFetched: number;
        errorDetail: { message?: string } | null;
        updatedAt: string;
      }>;
      rawByEndpoint: Array<{ endpoint: string; count: number }>;
      canonByResourceType: Array<{ resourceType: string; count: number }>;
      invalidByEndpoint: Array<{ endpoint: string; count: number }>;
      totals: { rawRecords: number; canonicalRecords: number; invalidRecords: number };
    }>(`/api/sessions/${id}/cache/summary`),
  getCacheRecords: (id: string, endpoint: string, opts?: { limit?: number; offset?: number }) => {
    const params = new URLSearchParams({ endpoint });
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts?.offset !== undefined) params.set('offset', String(opts.offset));
    return request<{
      records: Array<{
        id: string;
        sourceRecordId: string;
        url: string;
        responseStatus: number;
        fetchedAt: string;
        bodyInline: boolean;
        bodyJson: unknown;
        canonical: { resourceType: string; data: unknown } | null;
        invalid: { error: string; validationIssues: unknown } | null;
      }>;
      totalCount: number;
      limit: number;
      offset: number;
    }>(`/api/sessions/${id}/cache/records?${params.toString()}`);
  },
  getCacheInvalid: (id: string, endpoint?: string) => {
    const params = new URLSearchParams();
    if (endpoint) params.set('endpoint', endpoint);
    return request<{
      records: Array<{
        id: string;
        endpoint: string;
        sourceRecordId: string;
        resourceType: string;
        errorMessage: string;
        validationIssues: unknown;
        transformJsonata: string | null;
        rawRecord: unknown;
        createdAt: string;
      }>;
    }>(`/api/sessions/${id}/cache/invalid${params.toString() ? `?${params.toString()}` : ''}`);
  },
  getCacheNetwork: (id: string, opts?: { endpoint?: string; limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (opts?.endpoint) params.set('endpoint', opts.endpoint);
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts?.offset !== undefined) params.set('offset', String(opts.offset));
    return request<{
      requests: Array<{
        id: string;
        endpoint: string;
        sourceRecordId: string;
        url: string;
        method: string;
        status: number;
        durationMs: number;
        bytes: number;
        fetchedAt: string;
      }>;
      totalCount: number;
      limit: number;
      offset: number;
    }>(`/api/sessions/${id}/cache/network${params.toString() ? `?${params.toString()}` : ''}`);
  },
  getCacheRawRow: (id: string, rowId: string) =>
    request<{
      id: string;
      endpoint: string;
      sourceRecordId: string;
      url: string;
      method: string;
      requestHeaders: Record<string, string>;
      requestBody: string | null;
      responseStatus: number;
      responseHeaders: Record<string, string>;
      durationMs: number;
      bytes: number;
      bodyJson: unknown;
      bodyBlobPath: string | null;
      fetchedAt: string;
    }>(`/api/sessions/${id}/cache/raw/${rowId}`),

  // FHIR import (profiled-subset migration path → Cerbo)
  listFhirMappingSpecs: (sessionId: string) =>
    request<{ specs: Array<{ id: string; sessionId: string; sourceSystem: string; resourceType: string; spec: Record<string, unknown>; status: string; authoredBy: string; rationale: string | null; createdAt: string }> }>(`/api/sessions/${sessionId}/fhir-mapping-specs`),
  createFhirMappingSpec: (
    sessionId: string,
    spec: Record<string, unknown>,
    opts?: { status?: 'pending_review' | 'accepted'; authoredBy?: 'ai' | 'human' | 'fixture'; rationale?: string },
  ) =>
    request<{ specId: string }>(`/api/sessions/${sessionId}/fhir-mapping-spec`, {
      method: 'POST',
      body: JSON.stringify({ spec, ...opts }),
    }),
  acceptFhirMappingSpec: (sessionId: string, specId?: string) =>
    request<{ ok: true; specId: string }>(`/api/sessions/${sessionId}/fhir-mapping-spec/accept`, {
      method: 'POST',
      body: JSON.stringify(specId ? { specId } : {}),
    }),
  aiSuggestFhirSpec: (
    sessionId: string,
    headers: string[],
    opts?: { sourceSystem?: string; resourceType?: string; sampleRows?: Record<string, string>[] },
  ) =>
    request<{ spec: Record<string, unknown>; source: 'ai' | 'heuristic'; aiError?: string }>(
      `/api/sessions/${sessionId}/fhir-mapping-spec/ai-suggest`,
      { method: 'POST', body: JSON.stringify({ headers, ...opts }) },
    ),
  inferFhirMappingSpec: (sessionId: string, headers: string[], opts?: { sourceSystem?: string; resourceType?: string }) =>
    request<{ spec: Record<string, unknown> }>(`/api/sessions/${sessionId}/fhir-mapping-spec/infer`, {
      method: 'POST',
      body: JSON.stringify({ headers, ...opts }),
    }),
  // Multi-resource inference: every spec a wide file supports (Patient + a clinical
  // spec per signal), so one file can import as all its resources.
  inferFhirMappingSpecs: (sessionId: string, headers: string[], opts?: { sourceSystem?: string }) =>
    request<{ specs: Array<Record<string, unknown>> }>(`/api/sessions/${sessionId}/fhir-mapping-spec/infer`, {
      method: 'POST',
      body: JSON.stringify({ headers, multi: true, ...opts }),
    }),
  startFhirImport: (
    sessionId: string,
    body: { sourceSystem: string; files: Array<{ uploadId: string; specId?: string; spec?: Record<string, unknown> }>; destinationKind?: 'cerbo' | 'fhir-ndjson' | 'fhir-csv' },
  ) =>
    request<{ jobId: string; sessionId: string }>(`/api/sessions/${sessionId}/start-fhir-import`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ── Flat-file recipes (per-EHR reusable mappings; deterministic reuse) ──────
  // The recipe library for an EHR ("the recipe is always viewable").
  listEhrFlatFileRecipes: (ehrId: string) =>
    request<{ recipes: FlatFileRecipeRow[] }>(`/api/ehrs/${ehrId}/flat-file-recipes`),
  // Cross-EHR recipe library (top-level discoverability view).
  listAllFlatFileRecipes: (params?: { ehrId?: string; status?: string }) => {
    const q = new URLSearchParams(Object.entries(params ?? {}).filter(([, v]) => v != null) as [string, string][]).toString();
    return request<{ recipes: FlatFileRecipeRow[] }>(`/api/flat-file-recipes${q ? `?${q}` : ''}`);
  },
  getFlatFileRecipe: (recipeId: string) =>
    request<{ recipe: FlatFileRecipeRow; versions: FlatFileRecipeRow[] }>(`/api/flat-file-recipes/${recipeId}`),
  acceptFlatFileRecipe: (recipeId: string) =>
    request<{ ok: true; recipeId: string; superseded: string[] }>(`/api/flat-file-recipes/${recipeId}/accept`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  // Attach (find-or-create) the SOURCE EHR for a flat-file session — the anchor recipes hang off.
  attachSessionEhr: (sessionId: string, body: { ehrId?: string; slug?: string; displayName?: string }) =>
    request<{ ehr: { id: string; slug: string; displayName: string } }>(`/api/sessions/${sessionId}/attach-ehr`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // Fingerprint a file's headers → an accepted recipe for this session's EHR (the reuse path).
  lookupFlatFileRecipe: (sessionId: string, body: { headers: string[]; sourceSystem?: string }) =>
    request<{
      fingerprint: string;
      sourceSystem: string;
      recipe: FlatFileRecipeRow | null;
      reuse: boolean;
      alternatives: Array<{ id: string; label: string | null; version: number; headerFingerprint: string }>;
    }>(`/api/sessions/${sessionId}/flat-file-recipe/lookup`, { method: 'POST', body: JSON.stringify(body) }),
  // Deterministic health check (pre-launch gate): validate a recipe/specs against
  // a sample of the file → PASS/FAIL verdict (persists to recipe.health if recipeId).
  healthCheckRecipe: (
    sessionId: string,
    body: { uploadId: string; recipeId?: string; specs?: Array<Record<string, unknown>>; limit?: number },
  ) =>
    request<{ health: RecipeHealth }>(`/api/sessions/${sessionId}/flat-file-recipe/health-check`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // Re-scope (repair) a drifted/failing recipe from the current file → a new active
  // version (design §6). Deterministic re-infer; resolves the open recipe_health flag.
  rescopeFlatFileRecipe: (sessionId: string, body: { uploadId: string; sourceSystem?: string; label?: string }) =>
    request<{ recipeId: string; version: number; fingerprint: string; sourceSystem: string; health: RecipeHealth | null; specs: Array<Record<string, unknown>> }>(
      `/api/sessions/${sessionId}/flat-file-recipe/rescope`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  // Author a recipe version from accepted specs (requires an attached EHR).
  saveFlatFileRecipe: (
    sessionId: string,
    body: {
      headers: string[];
      sourceSystem?: string;
      specs: Array<Record<string, unknown>>;
      docRules?: Record<string, unknown>;
      label?: string;
      authoredBy?: 'ai' | 'human' | 'fixture';
      rationale?: string;
      status?: 'pending_review' | 'accepted';
      uploadId?: string; // when set, the health check runs at author time
    },
  ) =>
    request<{ recipeId: string; version: number; status: string; fingerprint: string; sourceSystem: string; health: RecipeHealth | null }>(
      `/api/sessions/${sessionId}/flat-file-recipe`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // ── preview / dry-run / common-schema cache (operator UI) ──────────────────
  getUploadPreview: (sessionId: string, uploadId: string, opts?: { limit?: number }) =>
    request<{ headers: string[]; rows: Array<Record<string, string>>; truncated: boolean; totalRowEstimate: number; extractError?: string | null }>(
      `/api/sessions/${sessionId}/uploads/${uploadId}/preview${opts?.limit ? `?limit=${opts.limit}` : ''}`,
    ),
  dryRunFhirSpec: (sessionId: string, body: { uploadId: string; spec: Record<string, unknown>; limit?: number }) =>
    request<{
      rows: Array<{
        rowNumber: number;
        sourceRow: Record<string, string>;
        resource: Record<string, unknown> | null;
        errors: Array<{ path: string; detail: string }>;
      }>;
      okCount: number;
      errorCount: number;
    }>(`/api/sessions/${sessionId}/fhir-mapping-spec/dry-run`, { method: 'POST', body: JSON.stringify(body) }),
  // Stage 3 preview: the destination (Cerbo) draft — the real table rows the
  // exporter would write, grouped by table (PT_ID + resolver FKs show as 0).
  cerboDraftFhirSpec: (sessionId: string, body: { uploadId: string; spec: Record<string, unknown>; limit?: number; notesTarget?: 'pt_notes' | 'soap' }) =>
    request<{
      tables: Array<{ table: string; rows: Array<{ rowNumber: number; columns: Record<string, unknown> }> }>;
      okCount: number;
      rowsProcessed: number;
    }>(`/api/sessions/${sessionId}/fhir-mapping-spec/cerbo-draft`, { method: 'POST', body: JSON.stringify(body) }),
  getFhirCache: (
    sessionId: string,
    opts?: { resourceType?: string; loadStatus?: string; limit?: number; offset?: number },
  ) => {
    const q = new URLSearchParams();
    if (opts?.resourceType) q.set('resourceType', opts.resourceType);
    if (opts?.loadStatus) q.set('loadStatus', opts.loadStatus);
    if (opts?.limit != null) q.set('limit', String(opts.limit));
    if (opts?.offset != null) q.set('offset', String(opts.offset));
    const qs = q.toString();
    return request<{
      rows: Array<{
        id: string;
        resourceType: string;
        fhirId: string;
        sourceRowRef: { uploadId?: string; rowNumber: number } | null;
        resource: Record<string, unknown>;
        mappingStatus: string;
        loadStatus: string | null;
        loadAction: string | null;
        destinationId: number | null;
        error: Record<string, unknown> | null;
      }>;
      totalCount: number;
      limit: number;
      offset: number;
    }>(`/api/sessions/${sessionId}/fhir-cache${qs ? `?${qs}` : ''}`);
  },
  getFhirCacheSummary: (sessionId: string) =>
    request<{
      byType: Array<{ resourceType: string; mapped: number; invalid: number; loaded: number; skipped: number; failed: number; notLoaded: number }>;
      totals: { mapped: number; invalid: number; loaded: number; skipped: number; failed: number; notLoaded: number };
    }>(`/api/sessions/${sessionId}/fhir-cache/summary`),

  // Slim roster of the session's mapped Patients (for the document-file picker).
  getSessionPatients: (sessionId: string) =>
    request<{ patients: Array<{ patientId: string; name: string; mrn: string; dob: string }> }>(`/api/sessions/${sessionId}/patients`),

  // ── documents: detect dropped files → patient, stage DocumentReferences ─────
  // The S3 locations a zip/7z drop expanded under (recorded by the expand job).
  getDocumentSources: (sessionId: string) =>
    request<{ sources: Array<{ bucket: string; prefix: string; dropId: string | null }> }>(
      `/api/sessions/${sessionId}/document-sources`,
    ),
  // Match files → patients + stage a DocumentReference each. With no body it
  // auto-targets every recorded source; unmatched files go to the review queue.
  detectDocuments: (sessionId: string, body?: { sourceSystem?: string; bucket?: string; prefix?: string }) =>
    request<{ jobIds: string[]; sessionId: string }>(`/api/sessions/${sessionId}/detect-documents`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),
  getImportConfig: (sessionId: string) =>
    request<{ importConfig: Record<string, unknown> }>(`/api/sessions/${sessionId}/import-config`),
  setImportConfig: (sessionId: string, importConfig: Record<string, unknown>) =>
    request<{ ok: true }>(`/api/sessions/${sessionId}/import-config`, { method: 'POST', body: JSON.stringify({ importConfig }) }),

  // S3 browser (cerbo-import-data / cerbo-client-data-prod)
  listStorageBuckets: () => request<{ buckets: StorageBucket[] }>('/api/storage/buckets'),
  listStorage: (bucket: string, prefix = '', token?: string) => {
    const p = new URLSearchParams({ bucket, prefix });
    if (token) p.set('token', token);
    return request<StorageListing>(`/api/storage/list?${p.toString()}`);
  },
  getStorageObject: (bucket: string, key: string) => {
    const p = new URLSearchParams({ bucket, key });
    return request<StorageObjectMeta>(`/api/storage/object?${p.toString()}`);
  },
  getStorageFolderSize: (bucket: string, prefix: string) => {
    const p = new URLSearchParams({ bucket, prefix });
    return request<{ bucket: string; prefix: string; bytes: number; objectCount: number; truncated: boolean; cached: boolean }>(
      `/api/storage/folder-size?${p.toString()}`,
    );
  },
  // Raw byte URL (same-origin, cookie-authed) for <img>/<iframe>/download links.
  storageDownloadUrl: (bucket: string, key: string, disposition?: 'inline' | 'attachment') => {
    const p = new URLSearchParams({ bucket, key });
    if (disposition) p.set('disposition', disposition);
    return `/api/storage/download?${p.toString()}`;
  },
};
